import React from "react";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { formatCurrency, formatNumber } from "./dashboardMetrics";

/*
 * Chart palette and chrome.
 *
 * Hues are validated for colour-vision deficiency against a white surface
 * (worst adjacent CVD ΔE 9.2, normal-vision ΔE 27.6 — both clear of the
 * thresholds). Aqua sits below 3:1 contrast on white, so every chart using it
 * ships visible axis values and a tooltip rather than relying on the fill alone.
 *
 * Each chart carries ONE series on ONE axis. Profit and load count are different
 * units and wildly different magnitudes, so they are separate charts rather than
 * a dual-axis overlay — the single most common way to make a chart lie.
 */
export const CHART = {
	profit: "#2a78d6", // blue
	volume: "#eb6834", // orange
	efficiency: "#1baf7a", // aqua
	grid: "#e1e0d9",
	axis: "#c3c2b7",
	muted: "#898781",
	ink: "#0b0b0b",
};

const axisProps = {
	stroke: CHART.axis,
	tick: { fill: CHART.muted, fontSize: 12 },
	tickLine: false,
};

/** Shared tooltip so hover reads identically across all three charts. */
const ChartTooltip = ({ active, payload, label, formatter, unitLabel }) => {
	if (!active || !payload || !payload.length) return null;
	return (
		<div
			style={{
				background: "#ffffff",
				border: "1px solid rgba(11,11,11,0.12)",
				borderRadius: 8,
				boxShadow: "0 6px 20px rgba(11,11,11,0.10)",
				padding: "8px 12px",
				fontSize: 13,
			}}
		>
			<div style={{ color: CHART.muted, marginBottom: 2 }}>{label}</div>
			<div style={{ color: CHART.ink, fontWeight: 600 }}>
				{formatter(payload[0].value)}
				<span style={{ color: CHART.muted, fontWeight: 400 }}> {unitLabel}</span>
			</div>
		</div>
	);
};

export const ProfitTrendChart = ({ series }) => (
	<ResponsiveContainer width="100%" height={260}>
		<AreaChart data={series} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
			<defs>
				<linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor={CHART.profit} stopOpacity={0.28} />
					<stop offset="100%" stopColor={CHART.profit} stopOpacity={0.02} />
				</linearGradient>
			</defs>
			<CartesianGrid stroke={CHART.grid} vertical={false} />
			<XAxis dataKey="label" {...axisProps} />
			<YAxis
				{...axisProps}
				width={64}
				tickFormatter={(v) => formatCurrency(v, { compact: true })}
			/>
			<Tooltip
				cursor={{ stroke: CHART.axis, strokeWidth: 1 }}
				content={<ChartTooltip formatter={(v) => formatCurrency(v)} unitLabel="profit" />}
			/>
			<Area
				type="monotone"
				dataKey="profit"
				stroke={CHART.profit}
				strokeWidth={2}
				fill="url(#profitFill)"
				dot={false}
				activeDot={{ r: 5, strokeWidth: 2, stroke: "#ffffff" }}
			/>
		</AreaChart>
	</ResponsiveContainer>
);

export const LoadVolumeChart = ({ series }) => {
	const peak = Math.max(...series.map((m) => m.loads), 0);
	return (
		<ResponsiveContainer width="100%" height={260}>
			<BarChart data={series} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
				<CartesianGrid stroke={CHART.grid} vertical={false} />
				<XAxis dataKey="label" {...axisProps} />
				<YAxis {...axisProps} width={48} allowDecimals={false} />
				<Tooltip
					cursor={{ fill: "rgba(11,11,11,0.04)" }}
					content={<ChartTooltip formatter={(v) => formatNumber(v)} unitLabel="loads" />}
				/>
				{/* 4px rounded top corners, anchored to the baseline. The busiest
				    month is emphasised by opacity, not a second hue — rank must never
				    change a series' colour identity. */}
				<Bar dataKey="loads" radius={[4, 4, 0, 0]} maxBarSize={34}>
					{series.map((m) => (
						<Cell
							key={m.month}
							fill={CHART.volume}
							fillOpacity={peak > 0 && m.loads === peak ? 1 : 0.62}
						/>
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};

export const AvgPerLoadChart = ({ series }) => (
	<ResponsiveContainer width="100%" height={260}>
		<LineChart data={series} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
			<CartesianGrid stroke={CHART.grid} vertical={false} />
			<XAxis dataKey="label" {...axisProps} />
			<YAxis
				{...axisProps}
				width={64}
				tickFormatter={(v) => formatCurrency(v, { compact: true })}
			/>
			<Tooltip
				cursor={{ stroke: CHART.axis, strokeWidth: 1 }}
				content={<ChartTooltip formatter={(v) => formatCurrency(v)} unitLabel="per load" />}
			/>
			<Line
				type="monotone"
				dataKey="avgPerLoad"
				stroke={CHART.efficiency}
				strokeWidth={2}
				dot={{ r: 3, strokeWidth: 0, fill: CHART.efficiency }}
				activeDot={{ r: 5, strokeWidth: 2, stroke: "#ffffff" }}
			/>
		</LineChart>
	</ResponsiveContainer>
);
